import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { SectionHeading, GradientButton } from '../../components/common/Primitives';
import VideoLightbox from '../../components/common/VideoLightbox';
import { youtubeThumb } from '../../lib/moduleVideos';
import { NEWS_ITEMS } from '../../lib/mock2';
import { ArrowRight, ExternalLink, PlayCircle } from 'lucide-react';

const NDTV_VIDEO_ID = 'Ej3q_85i2TE';

export default function News() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <PageShell>
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">News and media</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">Independent coverage, announcements and official media resources related to Gyan Dhara.</p>
        </div>
      </section>

      {/* FEATURED — NDTV coverage video */}
      <section className="px-6 pt-2 pb-8" data-testid="news-featured-video-section">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl border border-canvas-200 bg-white p-4 md:p-6 shadow-soft">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_1fr] items-center">
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-label="Play NDTV coverage of Gyan Dhara"
                data-testid="news-ndtv-play"
                className="group relative block w-full aspect-video rounded-2xl overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30"
              >
                <img
                  src={youtubeThumb(NDTV_VIDEO_ID, 'hqdefault')}
                  onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${NDTV_VIDEO_ID}/0.jpg`; }}
                  alt="NDTV coverage of Gyan Dhara"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 text-navy shadow-card group-hover:scale-105 transition-transform">
                    <PlayCircle className="h-9 w-9" />
                  </span>
                </span>
                <div className="absolute left-4 top-4 inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-navy backdrop-blur">NDTV coverage</div>
              </button>
              <div>
                <div className="text-[11.5px] font-bold tracking-[0.14em] text-purple">FEATURED VIDEO</div>
                <h3 className="mt-2 text-[24px] md:text-[28px] font-extrabold text-navy leading-tight">NDTV covers the Gyan Dhara project</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-ink-500">National television coverage on IIT Guwahati&rsquo;s VR-enabled learning platform being developed for PM SHRI schools in Assam.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="COVERAGE" title="Gyan Dhara in the media" desc="Independent coverage that builds institutional credibility." />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {NEWS_ITEMS.map((n, i) => (
              <a
                key={i}
                href={n.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-item-${i}`}
                className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft flex items-start gap-4 hover:-translate-y-0.5 hover:shadow-card transition-all focus:outline-none focus:ring-4 focus:ring-purple/20"
              >
                <div className={`h-10 w-10 rounded-lg border-2 border-${n.color} bg-${n.color}/10`} />
                <div className="flex-1">
                  <div className="text-[14.5px] font-bold text-navy flex items-center gap-1.5">
                    {n.title}
                    <ExternalLink className="h-3.5 w-3.5 text-ink-500" />
                  </div>
                  <p className="mt-1 text-[12.5px] text-ink-500">Read the coverage on {n.title}.</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="MEDIA RESOURCES" title="For journalists and partners" desc="A small downloadable press resource section replaces scattered project descriptions." />
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              { title: 'Project overview', desc: 'Approved one-page description of Gyan Dhara.', to: '/project-overview' },
              { title: 'Media contact', desc: 'Contact details for interviews and project information.', to: '/contact' },
            ].map((c, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
                <div className="text-[15px] font-bold text-navy">{c.title}</div>
                <p className="mt-1 text-[13px] text-ink-500">{c.desc}</p>
                <GradientButton to={c.to} variant="outline" className="mt-4 !px-3 !py-1.5 !text-royal !border-0 !bg-transparent">Open {c.title.toLowerCase()} <ArrowRight className="h-4 w-4" /></GradientButton>
              </div>
            ))}
          </div>
        </div>
      </section>

      {videoOpen && (
        <VideoLightbox
          videoId={NDTV_VIDEO_ID}
          title="NDTV coverage — Gyan Dhara"
          onClose={() => setVideoOpen(false)}
        />
      )}
    </PageShell>
  );
}
